import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, Empty } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Plus, Users, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { StudentProfile, sourceLabel } from "@/components/StudentProfile";
import { money } from "@/lib/api";

function AddStudent({ batchId, onDone }) {
  const [q, setQ] = useState("");
  const { data: cands } = useQuery({ queryKey: ["batch-cands", batchId, q], queryFn: async () => (await api.get(`/batches/${batchId}/candidates`, { params: { q } })).data });
  const add = useMutation({
    mutationFn: async (sid) => (await api.post(`/batches/${batchId}/students`, { student_id: sid })).data,
    onSuccess: () => { toast.success("Student added to batch"); setQ(""); onDone(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="batch-add-student">
      <Input placeholder="Add student — search by name or phone" value={q} onChange={(e) => setQ(e.target.value)} data-testid="batch-add-search" />
      {q && (
        <div className="max-h-44 overflow-y-auto space-y-1">
          {(cands || []).map((c) => (
            <div key={c.id} className="flex items-center justify-between text-sm border rounded px-2 py-1">
              <span>{c.name} <span className="text-xs text-slate-400">{c.phone} · {c.course_name || "—"} · {c.current_batch ? `in ${c.current_batch}` : "no batch"}</span></span>
              <Button size="sm" variant="outline" className="h-7" disabled={add.isPending} onClick={() => add.mutate(c.id)} data-testid={`batch-add-${c.id}`}>{c.current_batch ? "Move here" : "Add"}</Button>
            </div>
          ))}
          {!cands?.length && <p className="text-xs text-slate-400">No matching students</p>}
        </div>
      )}
    </div>
  );
}

function BatchRoster({ batchId, onClose }) {
  const [profileId, setProfileId] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["batch-roster", batchId], enabled: !!batchId, queryFn: async () => (await api.get(`/profiles/batch/${batchId}`)).data });
  const { data: allBatches } = useQuery({ queryKey: ["batches"], queryFn: async () => (await api.get("/batches")).data, enabled: isAdmin });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["batch-roster"] }); qc.invalidateQueries({ queryKey: ["batches"] }); };
  const onErr = (e) => toast.error(formatApiError(e.response?.data?.detail));
  const remove = useMutation({ mutationFn: async (sid) => (await api.delete(`/batches/${batchId}/students/${sid}`)).data, onSuccess: () => { toast.success("Removed from batch"); refresh(); }, onError: onErr });
  const move = useMutation({ mutationFn: async ({ sid, to }) => (await api.post(`/batches/${to}/students`, { student_id: sid })).data, onSuccess: () => { toast.success("Moved to new batch"); refresh(); }, onError: onErr });
  return (
    <Dialog open={!!batchId} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="batch-roster">
        <DialogHeader><DialogTitle>{data?.batch?.name || "Batch"} — {data?.students?.length ?? 0} student(s)</DialogTitle></DialogHeader>
        {isAdmin && batchId && <AddStudent batchId={batchId} onDone={refresh} />}
        {isLoading ? <Loading /> : !data?.students?.length ? <Empty title="No students in this batch yet" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase text-slate-500"><tr>
                <th className="text-left px-3 py-2">Student</th><th className="text-left px-3 py-2">Phone</th><th className="text-left px-3 py-2">Course</th>
                <th className="text-left px-3 py-2">Source</th><th className="text-left px-3 py-2">Sold by</th><th className="text-left px-3 py-2">Admission by</th>
                {data.sees_money && <><th className="text-left px-3 py-2">Paid</th><th className="text-left px-3 py-2">Balance</th></>}
                {isAdmin && <th className="text-left px-3 py-2">Actions</th>}
              </tr></thead>
              <tbody>
                {data.students.map((s) => (
                  <tr key={s.student_id} className="border-t" data-testid={`roster-row-${s.student_id}`}>
                    <td className="px-3 py-2"><button className="text-brand-blue hover:underline font-medium text-left" disabled={!s.admission_id} onClick={() => setProfileId(s.admission_id)} data-testid={`roster-open-${s.student_id}`}>{s.name}</button><div className="text-[11px] text-slate-400 font-mono">{s.admission_no}</div></td>
                    <td className="px-3 py-2">{s.phone}<div className="text-[11px] text-slate-400">{s.email}</div></td>
                    <td className="px-3 py-2 text-slate-500">{s.course_name || "—"}</td>
                    <td className="px-3 py-2 text-xs">{sourceLabel(s.lead_source)}</td>
                    <td className="px-3 py-2">{s.sold_by || "—"}</td>
                    <td className="px-3 py-2">{s.admission_by || "—"}</td>
                    {data.sees_money && <><td className="px-3 py-2 text-emerald-600">{money(s.paid)}</td><td className={`px-3 py-2 ${s.balance > 0 ? "text-red-600" : "text-emerald-600"}`}>{s.balance > 0 ? money(s.balance) : "Paid"}</td></>}
                    {isAdmin && (
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <Select onValueChange={(to) => move.mutate({ sid: s.student_id, to })}>
                            <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`roster-move-${s.student_id}`}><SelectValue placeholder="Move to batch" /></SelectTrigger>
                            <SelectContent>{(allBatches || []).filter((b) => b.id !== batchId).map((b) => <SelectItem key={b.id} value={b.id} disabled={b.available <= 0}>{b.name} ({b.available} left)</SelectItem>)}</SelectContent>
                          </Select>
                          <Button size="sm" variant="ghost" className="text-red-600 h-8" onClick={() => window.confirm(`Remove ${s.name} from this batch?`) && remove.mutate(s.student_id)} data-testid={`roster-remove-${s.student_id}`}>Remove</Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <StudentProfile admissionId={profileId} onClose={() => setProfileId(null)} />
      </DialogContent>
    </Dialog>
  );
}

export default function Batches() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [rosterId, setRosterId] = useState(null);
  const [form, setForm] = useState({ name: "", course_id: "", course_name: "", trainer_id: "", start_date: "", end_date: "", schedule: "", capacity: 15 });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const { data, isLoading } = useQuery({ queryKey: ["batches"], queryFn: async () => (await api.get("/batches")).data });
  const { data: courses } = useQuery({ queryKey: ["courses"], queryFn: async () => (await api.get("/settings/courses")).data, enabled: user.role === "admin" });
  const { data: staff } = useQuery({ queryKey: ["staff"], queryFn: async () => (await api.get("/users")).data, enabled: user.role === "admin" });

  const create = useMutation({
    mutationFn: async () => (await api.post("/batches", { ...form, trainer_id: form.trainer_id || null, capacity: Number(form.capacity) })).data,
    onSuccess: () => { toast.success("Batch created"); setOpen(false); qc.invalidateQueries(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const removeBatch = useMutation({
    mutationFn: async (id) => (await api.delete(`/batches/${id}`)).data,
    onSuccess: () => { toast.success("Batch deleted"); qc.invalidateQueries(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader title="Batches" subtitle="Capacity, schedule & enrolment"
        actions={user.role === "admin" &&
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-brand-blue hover:bg-blue-700 gap-2" data-testid="add-batch-btn"><Plus className="w-4 h-4" /> New Batch</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Batch</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Batch name *</Label><Input data-testid="batch-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
                <div><Label>Course</Label>
                  <Select value={form.course_id} onValueChange={(v) => { const c = courses.find((x) => x.id === v); set("course_id", v); set("course_name", c?.name || ""); }}>
                    <SelectTrigger data-testid="batch-course"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{(courses || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Trainer</Label>
                  <Select value={form.trainer_id || "none"} onValueChange={(v) => set("trainer_id", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="batch-trainer"><SelectValue placeholder="Assign trainer" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{(staff || []).filter((s) => s.role === "trainer").map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Start</Label><Input type="date" data-testid="batch-start" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
                  <div><Label>End</Label><Input type="date" data-testid="batch-end" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Schedule</Label><Input data-testid="batch-schedule" value={form.schedule} onChange={(e) => set("schedule", e.target.value)} placeholder="Mon-Sat 10-12" /></div>
                  <div><Label>Capacity</Label><Input type="number" data-testid="batch-capacity" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} /></div>
                </div>
              </div>
              <DialogFooter><Button className="bg-brand-blue hover:bg-blue-700" disabled={!form.name} onClick={() => create.mutate()} data-testid="batch-save">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } testid="batches-header" />

      {isLoading ? <Loading /> : !data?.length ? <Empty title="No batches" subtitle="Create a batch to enrol students." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((b) => (
            <div role="button" tabIndex={0} key={b.id} onClick={() => setRosterId(b.id)} onKeyDown={(e) => e.key === "Enter" && setRosterId(b.id)} className="relative cursor-pointer text-left p-5 rounded-xl border bg-white dark:bg-slate-800 hover:border-brand-blue hover:shadow-md transition-[border-color,box-shadow]" data-testid={`batch-${b.id}`}>
              {user.role === "admin" && (
                <Button size="icon" variant="ghost" className="absolute top-2 right-2 h-8 w-8 text-red-600" disabled={removeBatch.isPending}
                  onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete batch "${b.name}"?`)) removeBatch.mutate(b.id); }} data-testid={`batch-delete-${b.id}`}><Trash2 className="w-4 h-4" /></Button>
              )}
              <div className="font-head font-bold pr-8">{b.name}</div>
              <div className="text-sm text-slate-500">{b.course_name || "—"}</div>
              <div className="text-xs text-slate-400 mt-1">{b.schedule} • {b.start_date} → {b.end_date}</div>
              <div className="mt-3">
                <div className="flex justify-between text-xs mb-1"><span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {b.enrolled}/{b.capacity} enrolled</span><span className={b.available <= 0 ? "text-red-600 font-semibold" : "text-emerald-600"}>{b.available} seats</span></div>
                <Progress value={(b.enrolled / b.capacity) * 100} />
              </div>
              <div className="text-xs text-brand-blue mt-3">View students →</div>
            </div>
          ))}
        </div>
      )}
      <BatchRoster batchId={rosterId} onClose={() => setRosterId(null)} />
    </div>
  );
}
